import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import AppLauncher from './AppLauncher.jsx';
import { useOrbitAIAvailable } from '../orbit-ai/availability.js';
import ToolIcon from './ToolIcon.jsx';
import { OrbitMark } from './OrbitModuleHeader.jsx';
import { GROUPS, visibleModules } from '../modules/registry.js';
import { createApiClient } from '../http/createApiClient.js';
import ImpersonationBadge from '../auth/ImpersonationBadge.jsx';
import WorkspaceSelector from '../workspace/WorkspaceSelector.jsx';

const api = createApiClient({ auth: true });

// Tiles come from the module registry (shared/modules/registry.js), which is mirrored on the
// backend. Three hardcoded arrays lived here before; a module's section is now its `group`,
// and the order within a group is its order in the registry.
//
// The Hub renders one flat grid rather than a section per group — the group is a filter chip
// and a tile tag instead of a heading, so a search can cut across all three at once.

// Short tag shown on each tile, by registry group.
const GROUP_TAG = { admin: 'Admin', internal: 'Internal', client: 'Client' };

const ArrowGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h13M12 5l7 7-7 7"/>
  </svg>
);

// The matched run of the search term, marked inside the tile name. Only the first occurrence
// is highlighted — the name is short enough that a second one reads as noise.
function HighlightedName({ text, term }) {
  const i = term ? text.toLowerCase().indexOf(term) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + term.length)}</mark>
      {text.slice(i + term.length)}
    </>
  );
}

function ToolTile({ tool, term, index, innerRef }) {
  const clickable = tool.live && tool.path;

  const body = (
    <>
      <div className="o-hub-tile-head">
        <span className="o-hub-tile-icon"><ToolIcon type={tool.icon} /></span>
        <span className="o-hub-tag">{GROUP_TAG[tool.group] || 'Tool'}</span>
      </div>
      <h3 className="o-hub-tile-name">
        <HighlightedName text={tool.label} term={term} />
      </h3>
      <p className="o-hub-tile-desc">{tool.desc}</p>
      <div className="o-hub-tile-foot">
        <span className="o-hub-status">
          <span className="o-hub-dot" />
          {tool.live ? 'Live' : 'In build'}
        </span>
        {clickable
          ? <span className="o-hub-go">Open <ArrowGlyph /></span>
          : <span />}
      </div>
    </>
  );

  // Staggered entrance, matching the tile's position in the current result set.
  const style = { animationDelay: `${60 + index * 45}ms` };

  // A tile that is not live still takes a ref slot, so the slots stay aligned with `shown`;
  // keyboard navigation filters the non-buttons back out.
  if (!clickable) {
    return (
      <div ref={innerRef} className="o-hub-tile o-hub-soon o-hub-rise" style={style}
           aria-disabled="true">
        {body}
      </div>
    );
  }

  return (
    <button
      ref={innerRef}
      type="button"
      className="o-hub-tile o-hub-rise"
      style={style}
      onClick={() => {
        if (tool.sameTab) {
          history.pushState(null, '', tool.path);
          window.dispatchEvent(new PopStateEvent('popstate'));
        } else {
          window.open(tool.path, '_blank');
        }
      }}
    >
      {body}
    </button>
  );
}

// Placeholders cycle while the field is idle, as a hint at what is searchable.
const PROMPTS = [
  'Search tools…',
  'Try "taxonomy"…',
  'Try "share of voice"…',
  'Try "reset a password"…',
  'Try "reach and frequency"…',
];

export default function HubPage() {
  const { user, logout } = useAuth();
  const orbitAIAvailable = useOrbitAIAvailable();
  const initials = (user?.fullName || user?.username || '?')
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const firstName = (user?.fullName || user?.username || '').trim().split(/\s+/)[0] || '';

  // Tiles follow the caller's grants. Until now this page rendered all of MODULES to everyone,
  // so a tool you could not open still had a tile — harmless while every tile was broadly
  // shared, misleading now that Administration holds tools most people cannot use.
  //
  // `null` means "not answered yet": rendering the unfiltered list in the meantime would flash
  // tiles that are about to disappear, and rendering nothing would flash an empty Hub.
  const [grants, setGrants] = useState(null);
  const [permissionError, setPermissionError] = useState(false);
  const [permissionAttempt, setPermissionAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setPermissionError(false);
    api.get('/portal/modules')
      .then(d => { if (alive) setGrants({ held: d.grants || {}, admin: !!d.isGlobalAdmin, actingAs: d.actingAs }); })
      .catch(() => { if (alive) { setGrants(null); setPermissionError(true); } });
    return () => { alive = false; };
  }, [permissionAttempt]);

  const visible = useMemo(
    () => (grants ? visibleModules(grants.held, grants.admin, grants.actingAs) : []).filter(m => m.id !== 'orbit-ai' || orbitAIAvailable),
    [grants, orbitAIAvailable],
  );

  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('all');
  const [placeholder, setPlaceholder] = useState(PROMPTS[0]);
  const [dimPlaceholder, setDimPlaceholder] = useState(false);

  const inputRef = useRef(null);
  // One ref per rendered tile button, so arrow keys can walk the visible results.
  const tileRefs = useRef([]);

  const term = query.trim().toLowerCase();

  const shown = useMemo(() => visible.filter(m => {
    if (cat !== 'all' && m.group !== cat) return false;
    if (!term) return true;
    const hay = `${m.label} ${m.desc || ''} ${GROUP_TAG[m.group] || ''} ${m.id}`.toLowerCase();
    return hay.includes(term);
  }), [visible, cat, term]);

  tileRefs.current.length = shown.length;

  const counts = useMemo(() => {
    const c = { all: visible.length };
    for (const g of GROUPS) c[g.id] = visible.filter(m => m.group === g.id).length;
    return c;
  }, [visible]);

  // ⌘K / Ctrl-K focuses the field, Escape clears then blurs it, a bare printable key starts
  // typing into it, and the arrows walk the results. Bound on document because the Hub has no
  // single focus container — the user may be anywhere on the page when they start typing.
  useEffect(() => {
    function onKeyDown(e) {
      if (e.target.closest('[data-app-launcher]')) return;
      const meta = e.metaKey || e.ctrlKey;
      const input = inputRef.current;

      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input?.focus();
        input?.select();
        return;
      }

      if (e.key === 'Escape') {
        if (query) setQuery('');
        else input?.blur();
        return;
      }

      if (!meta && !e.altKey && e.key.length === 1 && document.activeElement !== input) {
        // Don't steal keystrokes from another field (the workspace selector, a future filter).
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        input?.focus();
        return;
      }

      // Only the live tiles are buttons, and only they can take focus.
      const openable = tileRefs.current.filter(el => el && el.tagName === 'BUTTON');

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!openable.length) return;
        e.preventDefault();
        const i = openable.indexOf(document.activeElement);
        const next = e.key === 'ArrowDown'
          ? (i < 0 ? 0 : Math.min(i + 1, openable.length - 1))
          : (i <= 0 ? 0 : i - 1);
        openable[next]?.focus();
        return;
      }

      if (e.key === 'Enter' && document.activeElement === input) {
        openable[0]?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [query]);

  // Idle placeholder rotation. Pauses while the field is focused or holds a query, and never
  // runs at all under prefers-reduced-motion.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let i = 0;
    let swap;
    const tick = setInterval(() => {
      if (document.activeElement === inputRef.current || inputRef.current?.value) return;
      i = (i + 1) % PROMPTS.length;
      setDimPlaceholder(true);
      swap = setTimeout(() => { setPlaceholder(PROMPTS[i]); setDimPlaceholder(false); }, 300);
    }, 3600);
    return () => { clearInterval(tick); clearTimeout(swap); };
  }, []);

  return (
    <div className="o-hub-root">
      <div className="o-hub-bg" aria-hidden="true">
        <span className="o-hub-blob o-hub-b1" />
        <span className="o-hub-blob o-hub-b2" />
        <span className="o-hub-blob o-hub-b3" />
        <span className="o-hub-blob o-hub-b4" />
        <span className="o-hub-blob o-hub-b5" />
        <svg className="o-hub-ring" viewBox="0 0 1080 1080">
          <g>
            <ellipse cx="540" cy="540" rx="520" ry="180" transform="rotate(-18 540 540)"/>
            <ellipse className="o-hub-ring-2" cx="540" cy="540" rx="400" ry="142"
                     transform="rotate(14 540 540)"/>
          </g>
        </svg>
      </div>

      <nav className="o-topnav o-hub-nav">
        <div className="o-topnav-start">
          <AppLauncher />
          <div className="o-brand">
            <OrbitMark />
            <span>Orbit</span>
          </div>
        </div>
        {/* The Hub hand-rolls its nav rather than using OrbitModuleHeader (it carries the
            avatar and Sign out), so the selector and the impersonation badge mount directly. */}
        <div className="o-topnav-right">
          <ImpersonationBadge />
          <WorkspaceSelector />
        </div>
        <div className="o-hub-user">
          <span className="o-avatar">{initials}</span>
          <span className="o-hub-name">{user?.fullName || user?.username}</span>
          <button className="o-hub" onClick={logout}>Sign out</button>
        </div>
      </nav>

      <section className="o-hub-hero">
        <h1>
          {firstName
            ? <>What are you working on, <em>{firstName}?</em></>
            : <>What are you <em>working on?</em></>}
        </h1>

        <div className="o-hub-cmd">
          <div className="o-hub-cmd-shell">
            <svg className="o-hub-cmd-icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
                 strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>
            </svg>
            <input
              ref={inputRef}
              type="text"
              autoComplete="off"
              spellCheck="false"
              aria-label="Search tools and dashboards"
              placeholder={placeholder}
              style={{ opacity: dimPlaceholder ? 0.35 : 1 }}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            {query
              ? (
                <button className="o-hub-cmd-clear" type="button" aria-label="Clear search"
                        onClick={() => { setQuery(''); inputRef.current?.focus(); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18"/>
                  </svg>
                </button>
              )
              : <span className="o-hub-kbd">⌘K</span>}
          </div>
        </div>
      </section>

      <nav className="o-hub-filters" aria-label="Filter by category">
        <button className="o-hub-chip" type="button" aria-pressed={cat === 'all'}
                onClick={() => setCat('all')}>
          All <span className="o-hub-n">{counts.all}</span>
        </button>
        {GROUPS.filter(g => counts[g.id] > 0).map(g => (
          <button key={g.id} className="o-hub-chip" type="button" aria-pressed={cat === g.id}
                  onClick={() => setCat(g.id)}>
            {g.title} <span className="o-hub-n">{counts[g.id]}</span>
          </button>
        ))}
      </nav>

      <main className="o-hub-main">
        {permissionError && <div className="o-state o-error" role="alert">Could not load your tools. Try again.
          <button className="o-btn o-btn-ghost" onClick={() => setPermissionAttempt(v => v + 1)}>Retry</button>
        </div>}
        <div className="o-hub-meta">
          <span>Showing {shown.length} of {visible.length}</span>
          <span>↑↓ navigate &nbsp;·&nbsp; ↵ open</span>
        </div>

        {shown.length > 0 && (
          <div className="o-hub-grid">
            {shown.map((tool, i) => (
              <ToolTile
                key={tool.id}
                tool={tool}
                term={term}
                index={i}
                innerRef={el => { tileRefs.current[i] = el; }}
              />
            ))}
          </div>
        )}

        {/* Two distinct empty states. A user can legitimately hold no module grants at all —
            a module admin revoking their only grant leaves them exactly here — and that is a
            different problem from a search that matched nothing. */}
        {grants && visible.length === 0 && (
          <div className="o-hub-empty">
            <h3>No tools have been shared with you yet</h3>
            <p>Ask an administrator for access to the tools you need.</p>
          </div>
        )}

        {visible.length > 0 && shown.length === 0 && (
          <div className="o-hub-empty">
            <h3>No modules match that</h3>
            <p>
              Try a different term, or <b>clear the search</b> to see all {visible.length} modules.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
